import React from 'react';
import styled from 'styled-components';
import { useEditMode } from '../../shared/hooks/useEditMode';
import { WorkViewerInfo, WorkViewerTitle } from '../../shared/component/headless/input/InputBody';
import { useTextViewStore } from '../../shared/store/TextViewStore';
import { useTextListActions } from '../../shared/hooks/useApi/useTextList';
import { ModalType, TextBlockType } from '../../shared/enum/EnumRepository';
import { useValidation } from '../../shared/hooks/useValidation';
import TextBlockList from './TextBlockList';
import MoleculeInputDiv from '../../shared/component/molecule/MoleculeInputDiv';
import { useTextBlock } from '../../shared/hooks/useApi/useTextBlock';
import EmptyTextViewer from './EmptyTextViewer';
import createIcon from '../../asset/icon/create.png';
import reorderIcon from '../../asset/icon/reorder.png';
import HintWrapper from '../../shared/component/headless/button/HintWrapper';
import { getHintMessage } from '../../shared/constants/hintMessages';
import { useModalStore } from '../../shared/store/portal/modalStore';
import { useAuth } from '../../shared/hooks/useApi/useAuth';
import { INPUT_YEAR_MIN_LENGTH, INPUT_YEAR_MAX_LENGTH } from '../../shared/constants/limits';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import MoleculeDivToggle from '../../shared/component/molecule/MoleculeDivToggle';
import RelatedProjectList from './RelatedProjectListInText';
import { toUpdateTextReq } from '../../shared/util/textTag';
import TextTagInViewer from './TextTagInViewer';
import { useTextTagStore } from '../../shared/store/textTagStore';
import { usePageOwnerIdentityStore } from '../../shared/store/pageOwnerIdentityStore';
import { canShowTextTagList } from '../../shared/util/visibility/textTagVisibility';


const TextViewer: React.FC = () => {
  const { isEditMode } = useEditMode();
  const { isOwner } = useAuth();
  const activeText = useTextViewStore((s) => s.activeText);
  const textHasChanged = useTextViewStore((s) => s.hasChanged);
  const activeTextBlockList = useTextViewStore((s) => s.activeTextBlockList);
  const handleChange = useTextViewStore((s) => s.updateActiveText);
  const { toggleTextAccess } = useTextListActions();
  const { createTextBlock } = useTextBlock();
  const { checkType } = useValidation();
  const { setStandardModal } = useModalStore();
  const tags = useTextTagStore((s) => s.tags);
  const listOwnerAui = useTextTagStore((s) => s.listOwnerAui);
  const tagStatus = useTextTagStore((s) => s.status);
  const pageOwner = usePageOwnerIdentityStore((s) => s.pageOwner);
  const canShowTags = canShowTextTagList({
    pageOwnerAui: pageOwner?.aui,
    listOwnerAui,
    status: tagStatus,
  });
  const visibleTags = canShowTags ? tags : [];

  // Text의 변경사항 또는 TextBlock 중 하나라도 변경된 경우
  const hasChanged = textHasChanged || activeTextBlockList.some(tb => tb.hasChanged);

  
  if (!activeText) return <EmptyTextViewer />;

  const handleChangeWithValidate = (
    field: 'title' | 'subtitle' | 'prodYear' | 'author',
    value: string 
  ) => {
    if (field === 'author') {
      handleChange({ 
        source: { ...activeText.source, author: value as string } 
      });
    }
    if (field === 'prodYear' && !checkType(field, value as string)) {
      return;
    };
    handleChange({ [field]: value });
  }

  const handleIsPublicToggle = async (value: boolean) => {
    if (!activeText) return;
    if (!isEditMode) return;

    await toggleTextAccess(
      toUpdateTextReq({ ...activeText, isPublic: value }, { includeTagIds: false }),
    );
  };

  const handleAddTextBlock = async () => {
    await createTextBlock({
      textId: activeText.id,
      type: TextBlockType.PARAGRAPH,
    });
  };

  const handleReOrderTextBlock = () => {
    setStandardModal({
      modalType: ModalType.INDEXING,
      title: "Text",
      value: null,
      handleChange: () => { },
    });
  };

  return (
    <>
      <TextInfoContainer>
        <MoleculeInputDiv
          value={activeText.title}
          defaultValue={"Title"}
          DefaultDiv={TitleDefaultDiv}
          placeholder={"Title"}
          handleChange={(e) => handleChangeWithValidate("title", e.target.value)}
          inputStyle={WorkViewerTitle}
          StyledDiv={Title}
        />
        <TextInfo>
          {(activeText.subtitle || isEditMode ) &&
          <TextInfoRow>
            <TextInfoLabel>Subtitle</TextInfoLabel>
            <MoleculeInputDiv
              value={activeText.subtitle || ""}
              defaultValue={"Subtitle"}
              DefaultDiv={DefaultDiv}
              placeholder={"Subtitle"}
              handleChange={(e) => handleChangeWithValidate("subtitle", e.target.value)}
              inputStyle={WorkViewerInfo}
              StyledDiv={Info}
              />
          </TextInfoRow>
          }
          <TextInfoRow>
            <TextInfoLabel>Author</TextInfoLabel>
            <MoleculeInputDiv
              value={activeText.source.author}
              defaultValue={"Author"}
              DefaultDiv={DefaultDiv}
              placeholder={"Author"}
              handleChange={(e) => handleChangeWithValidate("author", e.target.value)}
              inputStyle={WorkViewerInfo}
              StyledDiv={Info}
              />
          </TextInfoRow>
          <TextInfoRow>
            <TextInfoLabel>Year</TextInfoLabel>
            <MoleculeInputDiv
              value={activeText.prodYear}
              defaultValue={"Year"}
              DefaultDiv={DefaultDiv}
              placeholder={"Year"}
              handleChange={(e) => handleChangeWithValidate("prodYear", e.target.value)}
              inputStyle={WorkViewerInfo}
              StyledDiv={Info}
              minLimit={INPUT_YEAR_MIN_LENGTH}
              maxLimit={INPUT_YEAR_MAX_LENGTH}
              />
          </TextInfoRow>
          {(isEditMode || (activeText.tags?.length ?? 0) > 0) && (
            <TextInfoTags>
              <TextInfoLabel>Label</TextInfoLabel>
              <TagContainer>
                {visibleTags.map((tag) =>
                  <TextTagInViewer key={tag.id} tag={tag} />
                )}
              </TagContainer>
            </TextInfoTags>
          )}
        </TextInfo>
        {isEditMode && isOwner && (
          <TextAccessToggles>
            <MoleculeDivToggle
              value={activeText.isPublic}
              name={activeText.isPublic ? 'Public' : 'Private'}
              handleToggle={(e) => handleIsPublicToggle(e.target.checked)}
            />
          </TextAccessToggles>
        )}
      </TextInfoContainer>
      {isEditMode ? (
        <BlockSection>
          <BtnContainer
            data-has-changed={hasChanged ? "true" : "false"}   // 편집 모드 진입 시 변경사항이 있으면 스크롤>
          >
            <HintWrapper content={getHintMessage('ADD_TEXT_BLOCK')} toLeft={true}>
              <UniversalIconBtn 
                size="fit" 
                border={false} 
                backgroundImg={createIcon} 
                onClick={handleAddTextBlock} />
            </HintWrapper>
            <HintWrapper content={getHintMessage('REORDER_TEXT_BLOCKS')} toLeft={true}>
              <UniversalIconBtn 
                size="fit" 
                border={false} 
                backgroundImg={reorderIcon} 
                onClick={handleReOrderTextBlock} />
            </HintWrapper>
          </BtnContainer>
          <TextBlockList />
        </BlockSection>
      ) : (
        <TextBlockList />
      )}
      <RelatedProjectList />
    </>
  );
}


const TextInfoContainer = styled.div`
  display: flex;
  flex-direction: column;

  padding: 8px 0;

  gap: 2px;
`

const Title = styled.h2`
  width: 100%;
  height:28px;

  display: flex;
  align-items: center;

  margin-bottom: 10px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_03_1};
`;

const TitleDefaultDiv = styled.div`
  width: 100%;

  display: flex;
  align-items: center;

  margin-bottom: 10px;
  color: ${({ theme }) => theme.colors.color_Gray_05} !important;
  ${({ theme }) => theme.typography.Body_03_1};

  background-color: ${({ theme }) => theme.colors.color_Gray_06};
`;

const TextInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
`;

const Info = styled.div`
  flex: 1 1 auto;            /* 남은 공간 채움 */
  min-width: 0;              /* flex item 줄바꿈 허용 */
  text-align: left;          /* 읽기 쉽게 왼쪽 정렬 */
  white-space: normal;       /* 기본 줄바꿈 허용 */
  word-break: break-word;    /* 긴 단어 쪼개기 */
  overflow-wrap: anywhere;   /* 아주 긴 연속 문자열 강제 줄바꿈 */
  line-height: 1.2;          /* 여러 줄일 때 겹치지 않게 */
`;

const DefaultDiv = styled.div`
  width: fit-content;
  color: ${({ theme }) => theme.colors.color_Gray_05} !important;
  ${({ theme }) => theme.typography.Body_04};

  background-color: ${({ theme }) => theme.colors.color_Gray_06};
`;

const TextInfoRow = styled.div`
  height: 18px;
  min-height: 18px;
  margin-bottom: 1px;
  display: flex;
  align-items: center;
  gap: 8px;
  ${({ theme }) => theme.typography.Body_04};
`;

const TextInfoLabel = styled.span`
  min-width: 70px;
  padding-top: 2px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  font-weight: 500;
`;

const TextInfoTags = styled.div`
  height: fit-content;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  ${({ theme }) => theme.typography.Body_04};
`;

const TagContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 2px;
`;

const BlockSection = styled.div`
  width: 100%;
`;

const BtnContainer = styled.div`
  position: sticky;
  top: 0;
  z-index: 3;
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 4px 0;
  gap: 0.5vw;
`;

const TextAccessToggles = styled.div`
  display: flex;
  flex-direction: column;
`;

export default TextViewer;
